import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Shield, ShieldCheck, Star, Search, MapPin, Clock, DollarSign,
  Award, Briefcase, CheckCircle2, Loader2, User, ArrowRight,
  FileText, Edit, Upload, Settings, Calendar, ClipboardList, Trash2, AlertCircle, MessageCircle, Camera
} from 'lucide-react';
import api from '@/utils/api';
import { InspectorRegistrationModal, InspectorReviews } from '@/components/InspectorMarketplace';

// Specialization options
const SPECIALIZATIONS = [
  { id: 'all', label: 'All Categories' },
  { id: 'vehicle', label: 'Vehicles & Auto', icon: '🚗' },
  { id: 'equipment', label: 'Equipment', icon: '🔧' },
  { id: 'electronics', label: 'Electronics', icon: '📱' },
  { id: 'machinery', label: 'Machinery', icon: '⚙️' },
  { id: 'real_estate', label: 'Real Estate', icon: '🏠' },
  { id: 'collectibles', label: 'Collectibles & Art', icon: '🎨' },
  { id: 'general', label: 'General Merchandise', icon: '📦' },
  { id: 'handyman', label: 'Handyman', icon: '🛠️' }
];

const InspectorsPage = ({ user }) => {
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [specialization, setSpecialization] = useState('all');
  const navigate = useNavigate();
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [isInspector, setIsInspector] = useState(false);
  const [contactingInspector, setContactingInspector] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showUploadCert, setShowUploadCert] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);
  const [myInspections, setMyInspections] = useState([]);
  const [editFormData, setEditFormData] = useState({});
  const [uploadingCert, setUploadingCert] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [selectedInspectorForReviews, setSelectedInspectorForReviews] = useState(null);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [certData, setCertData] = useState({
    cert_type: '',
    cert_name: '',
    issuer: '',
    document: null
  });
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [showInspectorDetail, setShowInspectorDetail] = useState(false);

  useEffect(() => {
    fetchInspectors();
    if (user) {
      checkIfInspector();
    }
  }, [user, specialization]);

  const fetchInspectors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (specialization && specialization !== 'all') params.append('specialization', specialization);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await api.get(`/inspectors?${params.toString()}`);
      setInspectors(res.data.inspectors || []);
    } catch (error) {
      console.error('Failed to fetch inspectors:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkIfInspector = async () => {
    try {
      const res = await api.get('/inspector/profile');
      setIsInspector(true);
      setMyProfile(res.data);
      setEditFormData({
        bio: res.data.bio || '',
        hourly_rate: res.data.hourly_rate || '',
        flat_fee: res.data.flat_fee || '',
        service_areas: res.data.service_areas?.join(', ') || '',
        is_active: res.data.is_active !== false
      });
      // Fetch my inspections
      fetchMyInspections();
    } catch {
      setIsInspector(false);
    }
  };
  
  const fetchMyInspections = async () => {
    try {
      const res = await api.get('/inspector/inspections');
      setMyInspections(res.data.inspections || []);
    } catch (error) {
      console.error('Failed to fetch inspections:', error);
    }
  };
  
  const handleUpdateProfile = async () => {
    try {
      await api.put('/inspector/profile', {
        bio: editFormData.bio,
        hourly_rate: editFormData.hourly_rate ? parseFloat(editFormData.hourly_rate) : null,
        flat_fee: editFormData.flat_fee ? parseFloat(editFormData.flat_fee) : null,
        service_areas: editFormData.service_areas.split(',').map(s => s.trim()).filter(Boolean),
        is_active: editFormData.is_active
      });
      toast.success('Profile updated successfully');
      setShowEditProfile(false);
      checkIfInspector();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };
  
  const handleUploadProfileImage = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploadingProfileImage(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          await api.put('/inspector/profile', {
            profile_image: e.target.result
          });
          toast.success('Profile picture updated!');
          checkIfInspector();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
          setUploadingProfileImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingProfileImage(false);
    }
  };

  const handleContactInspector = async (inspector) => {
    if (!user) {
      toast.error('Please sign in to contact inspectors');
      return;
    }
    
    if (!inspector?.user_id) {
      toast.error('Unable to contact this inspector');
      return;
    }
    
    setContactingInspector(true);
    try {
      // Create or get existing conversation with the inspector
      const response = await api.post('/messages/conversation', {
        recipient_id: inspector.user_id
      });
      
      const conversationId = response.data.conversation_id;
      setShowInspectorDetail(false);
      
      // Navigate to messages with this conversation
      navigate(`/messages?conversation=${conversationId}`);
      toast.success(`Starting conversation with ${inspector.name}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
      // Fallback: just navigate to messages
      setShowInspectorDetail(false);
      navigate('/messages');
      toast.info(`Go to Messages to chat with ${inspector.name}`);
    } finally {
      setContactingInspector(false);
    }
  };
  
  const handleUploadCertification = async () => {
    if (!certData.cert_type || !certData.cert_name || !certData.document) {
      toast.error('Please fill in all certification fields');
      return;
    }
    
    setUploadingCert(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          await api.post('/inspector/certifications', {
            cert_type: certData.cert_type,
            name: certData.cert_name,
            issuer: certData.issuer,
            document_data: e.target.result
          });
          toast.success('Certification uploaded successfully! Pending review.');
          setShowUploadCert(false);
          setCertData({ cert_type: '', cert_name: '', issuer: '', document: null });
          checkIfInspector();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload certification');
        } finally {
          setUploadingCert(false);
        }
      };
      reader.readAsDataURL(certData.document);
    } catch (error) {
      toast.error('Failed to process file');
      setUploadingCert(false);
    }
  };
  
  const handleDeleteProfile = async () => {
    setDeletingProfile(true);
    try {
      await api.delete('/inspector/profile');
      toast.success('Inspector profile deleted successfully');
      setShowDeleteConfirm(false);
      setIsInspector(false);
      setMyProfile(null);
      setShowDashboard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete profile');
    } finally {
      setDeletingProfile(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInspectors();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-32">
      {/* Hero Section with Search */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-12 pb-6">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-4 mt-4">
              <div className="p-4 bg-white/20 rounded-full">
                <Shield className="h-12 w-12" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Professional Inspectors
            </h1>
            <p className="text-lg text-emerald-100 mb-8">
              Hire certified inspectors to verify items before you buy. 
              Get detailed reports on condition, authenticity, and value.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl mx-auto">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or location..."
                  className="pl-10 h-12 bg-white text-slate-800"
                />
              </div>
              <Select value={specialization} onValueChange={setSpecialization}>
                <SelectTrigger className="w-48 h-12 bg-white text-slate-800">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALIZATIONS.map(spec => (
                    <SelectItem key={spec.id} value={spec.id}>
                      {spec.icon} {spec.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" className="h-12 bg-white text-emerald-600 hover:bg-emerald-50">
                Search
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Search Results - Displayed immediately after search */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-800">
              {searchQuery || (specialization && specialization !== 'all')
                ? 'Search Results'
                : 'Available Inspectors'}
            </h2>
            <p className="text-slate-500 text-sm">{inspectors.length} found</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : inspectors.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-lg">
              <User className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <h3 className="text-lg font-semibold mb-1">No Inspectors Found</h3>
              <p className="text-slate-500 text-sm">
                {searchQuery || (specialization && specialization !== 'all')
                  ? 'Try adjusting your search filters'
                  : 'Be the first to register as an inspector!'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {inspectors.map(inspector => (
                <Card key={inspector.inspector_id} className="hover:shadow-lg transition-all hover:border-emerald-300 cursor-pointer group">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-12 w-12 border-2 border-emerald-100 group-hover:border-emerald-300 transition-colors">
                        <AvatarImage src={inspector.profile_image} />
                        <AvatarFallback className="bg-emerald-100 text-emerald-700 font-semibold">
                          {inspector.name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-semibold text-slate-800 truncate">{inspector.name}</h3>
                          {inspector.verification_status === 'verified' && (
                            <ShieldCheck className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                          <span>{inspector.rating?.toFixed(1) || 'New'}</span>
                          <span>•</span>
                          <span 
                            className="hover:text-emerald-600 cursor-pointer" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInspectorForReviews(inspector);
                              setShowReviewsModal(true);
                            }}
                          >
                            {inspector.review_count || 0} reviews
                          </span>
                          <span>•</span>
                          <span>{inspector.completed_inspections || 0} done</span>
                        </div>
                      </div>
                    </div>

                    {/* Bio snippet */}
                    {inspector.bio && (
                      <p className="text-xs text-slate-600 mt-2 line-clamp-2">{inspector.bio}</p>
                    )}

                    {/* Specializations */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {inspector.specializations?.slice(0, 3).map(spec => {
                        const specInfo = SPECIALIZATIONS.find(s => s.id === spec);
                        return (
                          <Badge key={spec} variant="outline" className="text-xs bg-slate-50">
                            {specInfo?.icon} {spec}
                          </Badge>
                        );
                      })}
                      {inspector.specializations?.length > 3 && (
                        <Badge variant="outline" className="text-xs bg-slate-50">
                          +{inspector.specializations.length - 3}
                        </Badge>
                      )}
                    </div>

                    {/* Service Areas */}
                    {inspector.service_areas?.length > 0 && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">{inspector.service_areas.slice(0, 2).join(', ')}</span>
                      </div>
                    )}

                    {/* Pricing & Action */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <div className="flex items-center gap-2">
                        {inspector.hourly_rate && (
                          <span className="text-emerald-600 font-semibold text-sm">${inspector.hourly_rate}/hr</span>
                        )}
                        {inspector.flat_fee && (
                          <span className="text-blue-600 font-semibold text-sm">${inspector.flat_fee} flat</span>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-emerald-500 hover:bg-emerald-600 text-white h-8 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedInspector(inspector);
                          setShowInspectorDetail(true);
                        }}
                      >
                        View <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Become an Inspector CTA */}
      {user && !isInspector && (
        <div className="container mx-auto px-4 -mt-8">
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 overflow-hidden">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <h3 className="font-bold text-2xl text-amber-800 mb-2">Join Our Professional Network</h3>
                <p className="text-amber-700">Earn money by helping buyers verify items. Set your own rates and schedule.</p>
              </div>
              
              {/* Specialty Badges */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                {[
                  { id: 'mechanic', icon: '🔧', title: 'Become a Mechanic', desc: 'Inspect vehicles & engines', color: 'from-blue-500 to-blue-600' },
                  { id: 'contractor', icon: '🏗️', title: 'Become a Contractor', desc: 'Inspect buildings & structures', color: 'from-orange-500 to-orange-600' },
                  { id: 'plumber', icon: '🔩', title: 'Become a Plumber', desc: 'Inspect plumbing systems', color: 'from-cyan-500 to-cyan-600' },
                  { id: 'electrician', icon: '⚡', title: 'Become an Electrician', desc: 'Inspect electrical systems', color: 'from-yellow-500 to-amber-500' },
                  { id: 'appraiser', icon: '💎', title: 'Become an Appraiser', desc: 'Appraise collectibles & art', color: 'from-purple-500 to-purple-600' },
                  { id: 'tech', icon: '📱', title: 'Become a Tech Expert', desc: 'Inspect electronics & gadgets', color: 'from-emerald-500 to-teal-500' },
                ].map((specialty) => (
                  <div
                    key={specialty.id}
                    onClick={() => setShowRegisterModal(true)}
                    className="group cursor-pointer"
                  >
                    <div className={`bg-gradient-to-br ${specialty.color} rounded-xl p-4 text-white text-center transform transition-all duration-300 hover:scale-105 hover:shadow-lg`}>
                      <div className="text-4xl mb-2">{specialty.icon}</div>
                      <h4 className="font-bold text-sm">{specialty.title}</h4>
                      <p className="text-xs text-white/80 mt-1">{specialty.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Choose Your Specialty CTA */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full mb-4">
                  <span className="text-amber-700">Or choose from 7+ other specialties</span>
                </div>
                <div>
                  <Button 
                    onClick={() => setShowRegisterModal(true)}
                    size="lg"
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg"
                  >
                    <Award className="h-5 w-5 mr-2" />
                    Choose Your Specialty & Register Now
                  </Button>
                </div>
                <p className="text-sm text-amber-600 mt-3">
                  ✓ Set your own rates &nbsp; ✓ Flexible schedule &nbsp; ✓ Earn on your terms
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* My Inspector Profile & Dashboard */}
      {isInspector && myProfile && (
        <div className="container mx-auto px-4 mt-8">
          {/* Profile Summary Card */}
          <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={myProfile.profile_image} />
                    <AvatarFallback className="bg-emerald-200 text-emerald-700 text-xl">
                      {myProfile.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-lg">{myProfile.name}</h3>
                      <Badge className={myProfile.verification_status === 'verified' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : myProfile.verification_status === 'rejected'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'}>
                        {myProfile.verification_status === 'verified' ? (
                          <><ShieldCheck className="h-3 w-3 mr-1" /> Verified</>
                        ) : myProfile.verification_status === 'rejected' ? (
                          <><Clock className="h-3 w-3 mr-1" /> Rejected</>
                        ) : (
                          <><Clock className="h-3 w-3 mr-1" /> Pending Verification</>
                        )}
                      </Badge>
                      <Badge variant="outline" className={myProfile.is_active !== false ? 'text-green-600' : 'text-slate-500'}>
                        {myProfile.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-slate-600">
                      {myProfile.completed_inspections || 0} inspections completed • 
                      ${myProfile.earnings?.total?.toFixed(2) || '0.00'} earned
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {myProfile.specializations?.map(spec => {
                        const specInfo = SPECIALIZATIONS.find(s => s.id === spec);
                        return (
                          <Badge key={spec} variant="outline" className="text-xs">
                            {specInfo?.icon} {specInfo?.label || spec}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    className="border-emerald-300 text-emerald-700"
                    onClick={() => setShowDashboard(!showDashboard)}
                    data-testid="toggle-inspector-dashboard"
                  >
                    <ClipboardList className="h-4 w-4 mr-2" />
                    {showDashboard ? 'Hide Dashboard' : 'View Dashboard'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowEditProfile(true)}
                    data-testid="edit-inspector-profile"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                  <Button 
                    onClick={() => setShowUploadCert(true)}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid="upload-certification"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Certification
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Expanded Dashboard */}
          {showDashboard && (
            <div className="space-y-6 mb-8">
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-emerald-600">{myProfile.completed_inspections || 0}</div>
                    <div className="text-sm text-slate-500">Completed</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-blue-600">{myInspections.filter(i => i.status === 'pending').length}</div>
                    <div className="text-sm text-slate-500">Pending</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-amber-600">${myProfile.earnings?.total?.toFixed(2) || '0.00'}</div>
                    <div className="text-sm text-slate-500">Total Earned</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-purple-600">{myProfile.rating?.toFixed(1) || 'N/A'}</div>
                    <div className="text-sm text-slate-500">Rating</div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Certifications Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      My Certifications
                    </span>
                    <Button size="sm" onClick={() => setShowUploadCert(true)}>
                      <Upload className="h-4 w-4 mr-1" /> Add New
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {myProfile.certifications?.length > 0 ? (
                    <div className="space-y-3">
                      {myProfile.certifications.map((cert, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <div className="font-medium">{cert.name}</div>
                            <div className="text-sm text-slate-500">
                              {cert.cert_type} • Issued by {cert.issuer || 'N/A'}
                            </div>
                          </div>
                          <Badge className={
                            cert.verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }>
                            {cert.verified ? 'Verified' : 'Pending Review'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      <p>No certifications uploaded yet</p>
                      <p className="text-sm">Upload your certifications to get verified</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Inspection Requests Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardList className="h-5 w-5" />
                    My Inspection Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {myInspections.length > 0 ? (
                    <div className="space-y-3">
                      {myInspections.slice(0, 5).map((inspection) => (
                        <div key={inspection.inspection_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <div className="font-medium">{inspection.listing_title || 'Inspection Request'}</div>
                            <div className="text-sm text-slate-500">
                              {inspection.inspection_type} inspection • {new Date(inspection.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <Badge className={
                            inspection.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            inspection.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                            inspection.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {inspection.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <ClipboardList className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      <p>No inspection requests yet</p>
                      <p className="text-sm">You'll see requests here once buyers hire you</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Edit Profile Dialog */}
          {showEditProfile && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <Card className="w-full max-w-lg bg-white my-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Edit Inspector Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Profile Picture Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Profile Picture</label>
                    <div className="flex items-center gap-4">
                      <Avatar className="h-20 w-20 border-2 border-emerald-200">
                        <AvatarImage src={myProfile?.profile_image} />
                        <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xl">
                          {myProfile?.name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <label className="cursor-pointer">
                          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg border border-emerald-200 transition-colors w-fit">
                            {uploadingProfileImage ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Camera className="h-4 w-4" />
                            )}
                            <span className="text-sm font-medium">
                              {uploadingProfileImage ? 'Uploading...' : 'Change Photo'}
                            </span>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleUploadProfileImage(e.target.files[0])}
                            disabled={uploadingProfileImage}
                          />
                        </label>
                        <p className="text-xs text-slate-500 mt-1">JPG, PNG. Max 5MB</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Bio</label>
                    <textarea
                      className="w-full p-2 border rounded-md"
                      rows={3}
                      value={editFormData.bio}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Describe your expertise..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Hourly Rate ($)</label>
                      <Input
                        type="number"
                        value={editFormData.hourly_rate}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                        placeholder="75"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Flat Fee ($)</label>
                      <Input
                        type="number"
                        value={editFormData.flat_fee}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, flat_fee: e.target.value }))}
                        placeholder="150"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Service Areas</label>
                    <Input
                      value={editFormData.service_areas}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, service_areas: e.target.value }))}
                      placeholder="New York, Los Angeles..."
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={editFormData.is_active}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="h-4 w-4"
                    />
                    <label htmlFor="is_active" className="text-sm">Available for new inspections</label>
                  </div>
                  
                  {/* Danger Zone */}
                  <div className="mt-6 pt-4 border-t border-red-200">
                    <p className="text-sm font-medium text-red-600 mb-2">Danger Zone</p>
                    <Button 
                      variant="outline" 
                      className="w-full border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => {
                        setShowEditProfile(false);
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Inspector Profile
                    </Button>
                  </div>
                </CardContent>
                <div className="flex justify-end gap-2 p-4 border-t">
                  <Button variant="outline" onClick={() => setShowEditProfile(false)}>Cancel</Button>
                  <Button onClick={handleUpdateProfile} className="bg-emerald-500 hover:bg-emerald-600">
                    Save Changes
                  </Button>
                </div>
              </Card>
            </div>
          )}
          
          {/* Delete Confirmation Dialog */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-5 w-5" />
                    Delete Inspector Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">
                    Are you sure you want to delete your inspector profile? This action cannot be undone.
                  </p>
                  <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700 border border-red-200">
                    <p className="font-medium">This will permanently:</p>
                    <ul className="list-disc ml-4 mt-1 space-y-1">
                      <li>Remove all your specializations</li>
                      <li>Delete your certifications</li>
                      <li>Cancel any pending inspection requests</li>
                      <li>Remove you from the inspectors list</li>
                    </ul>
                  </div>
                </CardContent>
                <div className="flex justify-end gap-2 p-4 border-t">
                  <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleDeleteProfile}
                    disabled={deletingProfile}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    {deletingProfile && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Yes, Delete Profile
                  </Button>
                </div>
              </Card>
            </div>
          )}
          
          {/* Upload Certification Dialog */}
          {showUploadCert && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-lg bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Upload Certification
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Certification Type *</label>
                    <Select value={certData.cert_type} onValueChange={(v) => setCertData(prev => ({ ...prev, cert_type: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="government_cert">Government Certification</SelectItem>
                        <SelectItem value="professional_license">Professional License</SelectItem>
                        <SelectItem value="insurance">Professional Insurance</SelectItem>
                        <SelectItem value="background_check">Background Check</SelectItem>
                        <SelectItem value="training_cert">Training Certificate</SelectItem>
                        <SelectItem value="portfolio">Portfolio/Experience Proof</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Certification Name *</label>
                    <Input
                      value={certData.cert_name}
                      onChange={(e) => setCertData(prev => ({ ...prev, cert_name: e.target.value }))}
                      placeholder="e.g., ASE Master Technician"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Issuing Authority</label>
                    <Input
                      value={certData.issuer}
                      onChange={(e) => setCertData(prev => ({ ...prev, issuer: e.target.value }))}
                      placeholder="e.g., National Institute for Automotive Service Excellence"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Document *</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setCertData(prev => ({ ...prev, document: e.target.files[0] }))}
                      className="w-full p-2 border rounded-md"
                    />
                    <p className="text-xs text-slate-500 mt-1">PDF, JPG, or PNG. Max 10MB</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                    <p className="font-medium">Important</p>
                    <p>Make sure the name on your certification matches your registered name: <strong>{myProfile.name}</strong></p>
                  </div>
                </CardContent>
                <div className="flex justify-end gap-2 p-4 border-t">
                  <Button variant="outline" onClick={() => setShowUploadCert(false)}>Cancel</Button>
                  <Button 
                    onClick={handleUploadCertification} 
                    className="bg-emerald-500 hover:bg-emerald-600"
                    disabled={uploadingCert}
                  >
                    {uploadingCert && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Upload Certification
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* How It Works */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
        <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {[
            { icon: Search, title: 'Find Inspector', desc: 'Browse verified inspectors by category and location' },
            { icon: Clock, title: 'Request Inspection', desc: 'Select an item and schedule an inspection' },
            { icon: Shield, title: 'Secure Payment', desc: 'Pay securely - funds held until report is delivered' },
            { icon: CheckCircle2, title: 'Get Report', desc: 'Receive detailed inspection report with photos' }
          ].map((step, i) => (
            <div key={i} className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <step.icon className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="font-semibold mb-1">{step.title}</h3>
              <p className="text-sm text-slate-600">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Serve Your Community - Always visible for all users */}
      {user && (
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white mb-3">Serve Your Community</h2>
              <p className="text-slate-300 text-lg">
                {isInspector 
                  ? 'Add more specialties to expand your services' 
                  : 'Join thousands of professionals earning on BidZuu'}
              </p>
            </div>
            
            {/* Professional Badges Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 max-w-6xl mx-auto mb-10">
              {[
                { id: 'vehicle', icon: '🔧', title: 'Mechanic', desc: 'Vehicles & Engines', gradient: 'from-blue-500 to-blue-700', requiresCert: true },
                { id: 'real_estate', icon: '🏗️', title: 'Contractor', desc: 'Buildings & Homes', gradient: 'from-orange-500 to-red-600', requiresCert: true },
                { id: 'equipment', icon: '🔩', title: 'Plumber', desc: 'Pipes & Systems', gradient: 'from-cyan-500 to-blue-600', requiresCert: true },
                { id: 'electronics', icon: '⚡', title: 'Electrician', desc: 'Wiring & Panels', gradient: 'from-yellow-400 to-orange-500', requiresCert: true },
                { id: 'collectibles', icon: '💎', title: 'Appraiser', desc: 'Art & Collectibles', gradient: 'from-purple-500 to-pink-600', requiresCert: true },
                { id: 'general', icon: '📱', title: 'Tech Expert', desc: 'Electronics', gradient: 'from-emerald-500 to-teal-600', requiresCert: true },
                { id: 'handyman', icon: '🛠️', title: 'Handyman', desc: 'General Help', gradient: 'from-slate-500 to-slate-700', requiresCert: false },
              ].map((prof, i) => {
                const alreadyHas = isInspector && myProfile?.specializations?.includes(prof.id);
                return (
                  <div 
                    key={i}
                    onClick={() => setShowRegisterModal(true)}
                    className={`bg-gradient-to-br ${prof.gradient} rounded-2xl p-5 text-center transform transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer relative ${alreadyHas ? 'ring-2 ring-white/50' : ''}`}
                  >
                    {alreadyHas && (
                      <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      </div>
                    )}
                    {!prof.requiresCert && (
                      <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                        No Cert
                      </div>
                    )}
                    <div className="text-5xl mb-3 drop-shadow-lg">{prof.icon}</div>
                    <h4 className="font-bold text-white text-lg">{prof.title}</h4>
                    <p className="text-white/70 text-sm">{prof.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* More Specialties */}
            <div className="text-center mb-8">
              <p className="text-slate-400 mb-4">Plus many more specialties:</p>
              <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
                {['HVAC Technician', 'Home Inspector', 'Marine Surveyor', 'Heavy Equipment', 'Motorcycle Expert', 'RV Inspector', 'Antique Specialist', 'Watch Expert', 'Jewelry Appraiser', 'Furniture Restorer'].map((spec, i) => (
                  <Badge key={i} variant="outline" className="bg-white/10 text-white border-white/20 px-3 py-1">
                    {spec}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* CTA */}
            <div className="text-center">
              <Button 
                onClick={() => setShowRegisterModal(true)}
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-lg px-8 py-6 rounded-full shadow-xl"
              >
                <Award className="h-6 w-6 mr-2" />
                {isInspector ? 'Add More Specialties' : 'Choose Your Specialty & Start Earning'}
              </Button>
              <div className="flex justify-center gap-6 mt-6 text-slate-400 text-sm">
                <span>✓ Set Your Own Rates</span>
                <span>✓ Flexible Schedule</span>
                <span>✓ Get Paid Securely</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Sign in prompt for guests */}
      {!user && (
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white mb-3">Serve Your Community</h2>
              <p className="text-slate-300 text-lg">Join thousands of professionals earning on BidZuu</p>
            </div>
            
            {/* Professional Badges Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 max-w-6xl mx-auto mb-10">
              {[
                { icon: '🔧', title: 'Mechanic', desc: 'Vehicles & Engines', gradient: 'from-blue-500 to-blue-700' },
                { icon: '🏗️', title: 'Contractor', desc: 'Buildings & Homes', gradient: 'from-orange-500 to-red-600' },
                { icon: '🔩', title: 'Plumber', desc: 'Pipes & Systems', gradient: 'from-cyan-500 to-blue-600' },
                { icon: '⚡', title: 'Electrician', desc: 'Wiring & Panels', gradient: 'from-yellow-400 to-orange-500' },
                { icon: '💎', title: 'Appraiser', desc: 'Art & Collectibles', gradient: 'from-purple-500 to-pink-600' },
                { icon: '📱', title: 'Tech Expert', desc: 'Electronics', gradient: 'from-emerald-500 to-teal-600' },
                { icon: '🛠️', title: 'Handyman', desc: 'General Help', gradient: 'from-slate-500 to-slate-700' },
              ].map((prof, i) => (
                <div 
                  key={i}
                  className={`bg-gradient-to-br ${prof.gradient} rounded-2xl p-5 text-center transform transition-all duration-300 hover:scale-105 hover:shadow-2xl`}
                >
                  <div className="text-5xl mb-3 drop-shadow-lg">{prof.icon}</div>
                  <h4 className="font-bold text-white text-lg">{prof.title}</h4>
                  <p className="text-white/70 text-sm">{prof.desc}</p>
                </div>
              ))}
            </div>

            {/* More Specialties */}
            <div className="text-center mb-8">
              <p className="text-slate-400 mb-4">Plus many more specialties:</p>
              <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
                {['HVAC Technician', 'Home Inspector', 'Marine Surveyor', 'Heavy Equipment', 'Motorcycle Expert', 'RV Inspector', 'Antique Specialist', 'Watch Expert', 'Jewelry Appraiser', 'Furniture Restorer'].map((spec, i) => (
                  <Badge key={i} variant="outline" className="bg-white/10 text-white border-white/20 px-3 py-1">
                    {spec}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Sign in CTA */}
            <div className="text-center">
              <p className="text-slate-400 mb-4">Sign in to register as an inspector</p>
              <Button 
                onClick={() => window.location.href = '/'}
                size="lg"
                className="bg-white text-slate-800 hover:bg-slate-100 text-lg px-8 py-6 rounded-full"
              >
                Sign In to Get Started
              </Button>
              <div className="flex justify-center gap-6 mt-6 text-slate-400 text-sm">
                <span>✓ Set Your Own Rates</span>
                <span>✓ Flexible Schedule</span>
                <span>✓ Get Paid Securely</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration Modal */}
      <InspectorRegistrationModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        existingProfile={isInspector ? myProfile : null}
        onSuccess={() => {
          setShowRegisterModal(false);
          checkIfInspector();
          if (isInspector) {
            toast.success('Specializations updated successfully!');
          } else {
            toast.success('Registration submitted! Please upload your certifications.');
          }
        }}
      />
      
      {/* Inspector Detail Modal */}
      <Dialog open={showInspectorDetail} onOpenChange={setShowInspectorDetail}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-500" />
              Inspector Profile
            </DialogTitle>
          </DialogHeader>
          {selectedInspector && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start gap-4">
                <Avatar className="h-20 w-20 border-4 border-emerald-100">
                  <AvatarImage src={selectedInspector.profile_image} />
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 text-2xl">
                    {selectedInspector.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-bold">{selectedInspector.name}</h2>
                    {selectedInspector.verification_status === 'verified' && (
                      <ShieldCheck className="h-5 w-5 text-emerald-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      <span className="font-medium">{selectedInspector.rating?.toFixed(1) || 'New'}</span>
                    </div>
                    <span>•</span>
                    <span>{selectedInspector.review_count || 0} reviews</span>
                    <span>•</span>
                    <span>{selectedInspector.completed_inspections || 0} inspections</span>
                  </div>
                  {selectedInspector.years_experience > 0 && (
                    <p className="text-sm text-slate-500 mt-1">
                      {selectedInspector.years_experience} years of experience
                    </p>
                  )}
                </div>
              </div>

              {/* Bio */}
              {selectedInspector.bio && (
                <div>
                  <h3 className="font-semibold mb-2">About</h3>
                  <p className="text-slate-600">{selectedInspector.bio}</p>
                </div>
              )}

              {/* Specializations */}
              <div>
                <h3 className="font-semibold mb-2">Specializations</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedInspector.specializations?.map(spec => {
                    const specInfo = SPECIALIZATIONS.find(s => s.id === spec);
                    return (
                      <Badge key={spec} className="bg-emerald-100 text-emerald-700 px-3 py-1">
                        {specInfo?.icon} {specInfo?.label || spec}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* Service Areas */}
              {selectedInspector.service_areas?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Service Areas</h3>
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="h-4 w-4" />
                    <span>{selectedInspector.service_areas.join(', ')}</span>
                  </div>
                </div>
              )}

              {/* Pricing */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3">Pricing</h3>
                <div className="flex gap-6">
                  {selectedInspector.hourly_rate && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                      <div>
                        <p className="font-bold text-lg text-emerald-600">${selectedInspector.hourly_rate}/hr</p>
                        <p className="text-xs text-slate-500">Hourly Rate</p>
                      </div>
                    </div>
                  )}
                  {selectedInspector.flat_fee && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-bold text-lg text-blue-600">${selectedInspector.flat_fee}</p>
                        <p className="text-xs text-slate-500">Flat Fee</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Certifications */}
              {selectedInspector.certifications?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Certifications</h3>
                  <div className="space-y-2">
                    {selectedInspector.certifications.map((cert, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-2 rounded">
                        <Award className="h-4 w-4 text-amber-500" />
                        <span className="font-medium">{cert.name || cert.type}</span>
                        {cert.issuer && <span className="text-slate-400">- {cert.issuer}</span>}
                        {cert.verification_status === 'verified' && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 ml-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => {
                    setSelectedInspectorForReviews(selectedInspector);
                    setShowInspectorDetail(false);
                    setShowReviewsModal(true);
                  }}
                >
                  <Star className="h-4 w-4 mr-2" /> View Reviews
                </Button>
                {user && (
                  <Button 
                    className="flex-1 bg-blue-500 hover:bg-blue-600"
                    onClick={() => handleContactInspector(selectedInspector)}
                    disabled={contactingInspector}
                  >
                    {contactingInspector ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <MessageCircle className="h-4 w-4 mr-2" />
                    )}
                    {contactingInspector ? 'Connecting...' : 'Message Inspector'}
                  </Button>
                )}
                {!user && (
                  <Button 
                    className="flex-1 bg-blue-500 hover:bg-blue-600"
                    onClick={() => {
                      setShowInspectorDetail(false);
                      toast.info('Please sign in to contact inspectors');
                    }}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" /> Message Inspector
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Reviews Modal */}
      <Dialog open={showReviewsModal} onOpenChange={setShowReviewsModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Reviews for {selectedInspectorForReviews?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedInspectorForReviews && (
            <InspectorReviews inspectorId={selectedInspectorForReviews.inspector_id} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InspectorsPage;
